import { useRef, useState } from "react"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import toast from "react-hot-toast"
import {FileDown} from 'lucide-react'
import type { EquipmentSpec, SpecItem } from "../hooks/useSpecs"

interface GroupedItems {
  category: string
  items: SpecItem[]
}

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" }) : "-"

/**
 * 오프스크린 엘리먼트를 A4 멀티페이지로 분할하여 PDF에 추가.
 */
async function renderToPdf(pdf: jsPDF, el: HTMLElement) {
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
  })
  const imgData = canvas.toDataURL("image/png")
  const pageWidth = 210
  const pageHeight = 297
  const imgWidth = pageWidth
  const imgHeight = (canvas.height * imgWidth) / canvas.width

  let heightLeft = imgHeight
  let position = 0

  pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
  heightLeft -= pageHeight

  while (heightLeft > 0) {
    position -= pageHeight
    pdf.addPage()
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight
  }
}

/**
 * 실제 제작 사양(actualSpec) 전용 PDF 출력 버튼 + 오프스크린 레이아웃.
 * 요구 사양/가이드라인/부품과 분리하여 <실제 제작 사양>만 표로 출력한다.
 */
export function ActualSpecPdfExport({
  spec,
  grouped,
}: {
  spec: EquipmentSpec
  grouped: GroupedItems[]
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [exporting, setExporting] = useState(false)

  const hasActual = grouped.some((g) => g.items.some((it) => (it.actualSpec || "").trim()))

  const handleExport = async () => {
    if (!ref.current) return
    if (grouped.length === 0) {
      toast.error("출력할 사양 항목이 없습니다.")
      return
    }
    setExporting(true)
    const loadingId = toast.loading("실제 제작 사양 PDF를 생성하는 중...")
    try {
      const pdf = new jsPDF("p", "mm", "a4")
      await renderToPdf(pdf, ref.current)
      const safeTitle = (spec.specTitle || "제작사양서").replace(/[\\/:*?"<>|]/g, "_")
      pdf.save(`${safeTitle}_실제제작사양_v${spec.currentVersion}.pdf`)
      toast.success("실제 제작 사양 PDF가 저장되었습니다.", { id: loadingId })
    } catch (e) {
      console.error("실제 제작 사양 PDF 생성 실패:", e)
      toast.error("PDF 생성에 실패했습니다.", { id: loadingId })
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <button
        onClick={handleExport}
        disabled={exporting}
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-60"
        title="실제 제작 사양 PDF 출력"
      >
        <FileDown className="w-4 h-4" />
        {exporting ? "생성 중..." : "실제사양 PDF"}
      </button>

      {/* ===== 오프스크린 인쇄 레이아웃 (A4 폭 794px) ===== */}
      <div
        style={{ position: "absolute", left: "-99999px", top: 0, width: "794px", background: "#ffffff" }}
        aria-hidden="true"
      >
        <div
          ref={ref}
          style={{
            width: "794px",
            padding: "40px",
            boxSizing: "border-box",
            fontFamily: "'Malgun Gothic', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif",
            color: "#111827",
            background: "#ffffff",
          }}
        >
          {/* 문서 헤더 */}
          <div style={{ borderBottom: "3px solid #10B981", paddingBottom: "16px", marginBottom: "20px" }}>
            <div style={{ fontSize: "12px", color: "#059669", fontWeight: 700, letterSpacing: "1px" }}>
              PAVETECH 실제 제작 사양
            </div>
            <div style={{ fontSize: "22px", fontWeight: 800, marginTop: "6px", lineHeight: 1.3 }}>
              {spec.specTitle}
            </div>
            <table style={{ width: "100%", marginTop: "14px", fontSize: "12px", color: "#374151" }}>
              <tbody>
                <tr>
                  <td style={{ padding: "3px 0", width: "90px", color: "#6B7280" }}>원청사</td>
                  <td style={{ padding: "3px 0", width: "40%" }}>{spec.sourceCompany || "-"}</td>
                  <td style={{ padding: "3px 0", width: "90px", color: "#6B7280" }}>문서번호</td>
                  <td style={{ padding: "3px 0" }}>{spec.docNumber || "-"}</td>
                </tr>
                <tr>
                  <td style={{ padding: "3px 0", color: "#6B7280" }}>현재 버전</td>
                  <td style={{ padding: "3px 0", fontWeight: 700, color: "#059669" }}>v{spec.currentVersion}</td>
                  <td style={{ padding: "3px 0", color: "#6B7280" }}>출력일</td>
                  <td style={{ padding: "3px 0" }}>{fmtDate(new Date().toISOString())}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 섹션 제목 */}
          <div style={{ fontSize: "15px", fontWeight: 800, color: "#065F46", marginBottom: "10px" }}>
            실제 제작 사양
          </div>

          {/* 실제 제작 사양 표 */}
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", tableLayout: "fixed" }}
          >
            <thead>
              <tr style={{ background: "#ECFDF5" }}>
                <th style={thStyle(120)}>대분류</th>
                <th style={thStyle(150)}>중분류</th>
                <th style={thStyle()}>실제 제작 사양</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((group) =>
                group.items.map((item, idx) => {
                  const isLastInGroup = idx === group.items.length - 1
                  return (
                    <tr key={item._id}>
                      {idx === 0 && (
                        <td
                          rowSpan={group.items.length}
                          style={{
                            ...cellBase,
                            borderTop: "1px solid #D1FAE5",
                            borderBottom: "1px solid #D1FAE5",
                            fontWeight: 700,
                            background: "#F0FDF4",
                            verticalAlign: "top",
                          }}
                        >
                          {group.category}
                        </td>
                      )}
                      <td
                        style={{
                          ...cellBase,
                          fontWeight: 600,
                          borderBottom: isLastInGroup ? "1px solid #D1FAE5" : "none",
                        }}
                      >
                        {item.subCategory}
                      </td>
                      <td
                        style={{
                          ...cellBase,
                          whiteSpace: "pre-wrap",
                          borderBottom: isLastInGroup ? "1px solid #D1FAE5" : "none",
                        }}
                      >
                        {(item.actualSpec || "").trim() || "-"}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>

          {!hasActual && (
            <div style={{ marginTop: "12px", fontSize: "11px", color: "#9CA3AF" }}>
              ※ 아직 입력된 실제 제작 사양이 없습니다.
            </div>
          )}

          {/* 푸터 */}
          <div style={{ marginTop: "24px", paddingTop: "12px", borderTop: "1px solid #E5E7EB", fontSize: "10px", color: "#9CA3AF", textAlign: "center" }}>
            페이브텍 관리 시스템 · 실제 제작 사양은 승인 절차 없이 현장 기입/수정되는 참고 자료입니다.
          </div>
        </div>
      </div>
    </>
  )
}

const thStyle = (width?: number): React.CSSProperties => ({
  border: "1px solid #D1FAE5",
  padding: "8px 6px",
  textAlign: "left",
  fontWeight: 700,
  color: "#065F46",
  ...(width ? { width: `${width}px` } : {}),
})

const cellBase: React.CSSProperties = {
  borderLeft: "1px solid #D1FAE5",
  borderRight: "1px solid #D1FAE5",
  padding: "8px 6px",
  verticalAlign: "top",
  lineHeight: 1.5,
}
